import { canNavigate, ConductorOneChildActive, Router, ScreenBase, splitUrlSegments } from "@frui.ts/screens";
import EventBus, { orderIsPaid, paymentMultiFuelTypeNotAllowed } from "services/eventBus";
import BankTransferViewModel, { BankTransferNavigationName } from "../common/payment/bankTransferViewModel";
import StatusViewModel, { StatusViewNavigationName } from "../common/payment/statusViewModel";
import BatchOrderViewModel, { BatchOrderViewModelNavigationName } from "./batchOrderViewModel";
import OrderRecapViewModel from "./orderRecapViewModel";
import OutageService from "../../services/outageService";
import { action, computed, reaction, when } from "mobx";
import OrderOutageViewModel from "../common/orderOutage/orderOutageViewModel";
import { SystemComponent } from "../../models/outage";
import OutagesViewModel from "../common/outagesViewModel";
import ChargeOrderService from "services/chargeOrderService";
import { ChargeOrderStatus } from "models/chargeOrder";
import EnumService from "services/enumService";
import ContactMultiEshopViewModel from "./contactMultiEshopViewModel";
import ChargeTypesService from "services/chargeTypesService";
import PaymentMultiEshopViewModel from "./paymentMultiEshopViewModel";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import TimeService from "services/timeService";

export const MultiEshopModuleNavigationName = "multi_eshop";

@Router.registerRoute({
  name: Router.Self,
  route: MultiEshopModuleNavigationName,
  children: [
    BatchOrderViewModel,
    OrderRecapViewModel,
    ContactMultiEshopViewModel,
    PaymentMultiEshopViewModel,
    StatusViewModel,
    BankTransferViewModel,
  ],
})
export default class MultiEshopViewModel extends ConductorOneChildActive<ScreenBase> {
  private unsubscriptions: Array<() => void> = [];
  public contactMultiEshopViewModel: ContactMultiEshopViewModel;
  public paymentMultiEshopViewModel: PaymentMultiEshopViewModel;
  payGateOutagesViewModel: OutagesViewModel;
  eshopOutagesVieModel: OutagesViewModel;

  @computed
  get eshopOutage() {
    return this.outageService.eshopActiveOutage;
  }

  @computed
  get showOutages() {
    return !(this.activeChild instanceof OrderOutageViewModel) && !(this.activeChild instanceof StatusViewModel);
  }

  constructor(
    public enumService: EnumService,
    private chargeOrderService: ChargeOrderService,
    private timeService: TimeService,
    private eventBus: EventBus,
    private batchOrderViewModel: BatchOrderViewModel,
    private orderRecapViewModel: OrderRecapViewModel,
    private chargeTypesService: ChargeTypesService,
    private paymentMultiEshopViewModelFactory: ReturnType<typeof PaymentMultiEshopViewModel.Factory>,
    private outageService: OutageService,
    private bankTransferVMFactory: ReturnType<typeof BankTransferViewModel.Factory>,
    private statusVMFactory: ReturnType<typeof StatusViewModel.Factory>,
    private orderOutageViewModelFactory: ReturnType<typeof OrderOutageViewModel.Factory>,
    private contactMultiEshopViewModelFactory: ReturnType<typeof ContactMultiEshopViewModel.Factory>,
    private outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>
  ) {
    super();
    this.navigationName = MultiEshopModuleNavigationName;
    this.nameValue = "multiEshop.title";
    this.payGateOutagesViewModel = this.outagesViewModelFactory(SystemComponent.PAY_GATE);
    this.eshopOutagesVieModel = this.outagesViewModelFactory(SystemComponent.ESHOP);
    this.children.push(
      this.batchOrderViewModel,
      this.orderRecapViewModel,
      (this.contactMultiEshopViewModel = this.contactMultiEshopViewModelFactory(
        new ChargeOrderContactItem(this.chargeOrder.notificationPreferences)
      )),
      (this.paymentMultiEshopViewModel = this.paymentMultiEshopViewModelFactory(this.chargeOrder))
    );
  }

  protected async onInitialize(): Promise<any> {
    await this.enumService.startInitialization();
    await this.timeService.getCurrentServerTime();

    await this.chargeTypesService.startInitialization();
    this.unsubscriptions.push(
      this.eventBus.subscribe(orderIsPaid, this.onOrderIsPaid),
      this.eventBus.subscribe(paymentMultiFuelTypeNotAllowed, this.onPaymentMultiFuelTypeNotAllowed),
      reaction(() => this.eshopOutage, this.onEshopOutageChanged)
    );

    return super.onInitialize();
  }

  protected async onActivate() {
    if (!this.activeChild || this.chargeOrderService.orderIsEmptyOrIncomplete) {
      this.lazyCheckEshopOutage();
      await this.tryActivateChild(this.batchOrderViewModel);
    }

    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  // tslint:disable-next-line:cognitive-complexity
  async navigate(subPath: string | undefined, params: any) {
    const segments = splitUrlSegments(subPath);
    let childToActivate;
    if (segments[0] === BankTransferNavigationName) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments[0] === StatusViewNavigationName && segments[1]) {
      const chargeOrder = this.chargeOrderService.chargeOrder;

      const result = params.result;
      const failureType = params["failure_type"];
      const failureOrigin = params["failure_origin"];

      if (chargeOrder) {
        await this.chargeOrderService.updateChargeOrderStatus(result, chargeOrder);
      }

      this.batchOrderViewModel.paymentResultFailureType = failureType;
      this.batchOrderViewModel.paymentResultFailureOrigin = failureOrigin;

      childToActivate = await this.findNavigationChild(subPath || "");
    } else {
      childToActivate = await this.findNavigationChild(segments[0] || "");
    }

    if (segments[0] !== BatchOrderViewModelNavigationName && this.activeChild instanceof BatchOrderViewModel) {
      const isValid = this.activeChild.validateAll();

      if (!isValid) return;
    }

    if (childToActivate) {
      await this.tryActivateChild(childToActivate);

      if (canNavigate(childToActivate)) {
        await childToActivate.navigate(segments[1], params);
      }
    }

    await this.onChildNavigated(childToActivate);
  }

  async findNavigationChild(navigationName: string) {
    if (navigationName.includes(BankTransferNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments[1]) {
        const chargeOrder = await this.chargeOrderService.getStoredChargeOrder(segments[1]);
        if (chargeOrder && chargeOrder._status === ChargeOrderStatus.PaidBankTransfer) {
          return this.bankTransferVMFactory(chargeOrder);
        }
      }
    } else if (navigationName.includes(StatusViewNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments[1]) {
        const chargeOrder = this.chargeOrderService.chargeOrder;
        if (
          chargeOrder &&
          (chargeOrder._status === ChargeOrderStatus.Paid || chargeOrder._status === ChargeOrderStatus.Deferred)
        ) {
          return this.statusVMFactory(chargeOrder);
        } else {
          return super.findNavigationChild(MultiEshopModuleNavigationName);
        }
      }
    }
    return super.findNavigationChild(navigationName);
  }

  private async lazyCheckEshopOutage() {
    await when(() => this.outageService.isInitialized === true);
    if (this.eshopOutage) {
      this.tryActivateChild(this.orderOutageViewModelFactory(this.eshopOutage));
    }
  }

  @computed
  get chargeOrder() {
    return this.chargeOrderService.chargeOrder;
  }

  @action.bound
  private onEshopOutageChanged() {
    if (this.eshopOutage === undefined) {
      this.tryActivateChild(this.batchOrderViewModel);
    }
  }

  @action.bound
  private onOrderIsPaid() {
    this.tryActivateChild(this.batchOrderViewModel);
  }

  @action.bound
  private onPaymentMultiFuelTypeNotAllowed() {
    this.chargeOrderService.chargeOrder.charges.forEach(charge => (charge._isModified = true));
    this.chargeOrderService.persistChargeOrder();
    this.tryActivateChild(this.orderRecapViewModel);
  }
}
